import { Copy, ExternalLink, FileText } from 'lucide-react'
import { useState } from 'react'
import { SiShopify } from 'react-icons/si'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

interface ShopifyPage {
  id: string
  title: string
  handle: string
  published: boolean
  publishedAt?: string | null
  url: string
}

interface ShopifyPagesListProps {
  pages: ShopifyPage[]
  shopDomain?: string
  hasNextPage?: boolean
  showCount?: boolean
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

const getPublishedStatusColor = (published: boolean) => {
  return published
    ? 'bg-green-100 text-green-700 border-green-200'
    : 'bg-yellow-100 text-yellow-700 border-yellow-200'
}

const getPublishedIcon = (published: boolean) => {
  return published ? '🟢' : '🟡'
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return null
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateString
  }
}

export function ShopifyPagesList({
  pages,
  shopDomain,
  hasNextPage = false,
  showCount = true,
}: ShopifyPagesListProps) {
  const [showDetails, setShowDetails] = useState(false)

  if (pages.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
        <FileText className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-600">No pages found</span>
      </div>
    )
  }

  const contextActions: ContextAction[] = [
    {
      id: 'copy-all-ids',
      label: 'Copy All Page IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(pages.map((p) => p.id).join('\n'))
      },
    },
    {
      id: 'copy-all-handles',
      label: 'Copy All Page Handles',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(pages.map((p) => p.handle).join('\n'))
      },
    },
    {
      id: 'copy-all-urls',
      label: 'Copy All Page URLs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(pages.map((p) => p.url).join('\n'))
      },
    },
    {
      id: 'copy-published-urls',
      label: 'Copy Published Page URLs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        const publishedUrls = pages.filter((p) => p.published).map((p) => p.url)
        await copyToClipboard(publishedUrls.join('\n'))
      },
    },
    ...(shopDomain
      ? [
          {
            id: 'open-pages-admin',
            label: 'Open Pages in Shopify Admin',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => {
              window.open(`https://${shopDomain}/admin/pages`, '_blank', 'noopener,noreferrer')
            },
          },
        ]
      : []),
  ]

  const publishedCount = pages.filter((p) => p.published).length
  const draftCount = pages.length - publishedCount

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="space-y-3">
        {showCount && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <SiShopify className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">
                  {pages.length} Page{pages.length === 1 ? '' : 's'}
                  {hasNextPage && ' (more available)'}
                </span>
              </div>
              {pages.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="flex items-center gap-1">🟢 {publishedCount} published</span>
                  {draftCount > 0 && (
                    <span className="flex items-center gap-1">🟡 {draftCount} draft</span>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
              <span className={`transform transition-transform ${showDetails ? 'rotate-90' : ''}`}>
                ▶
              </span>
            </button>
          </div>
        )}

        <div className="grid gap-2">
          {pages.map((page) => (
            <div key={page.id} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="truncate font-medium text-gray-900">{page.title}</h4>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getPublishedStatusColor(page.published)}`}
                    >
                      <span className="mr-1">{getPublishedIcon(page.published)}</span>
                      {page.published ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  <div className="space-y-0.5 text-xs text-gray-500">
                    <div>
                      Handle: <span className="font-mono">{page.handle}</span>
                    </div>
                    {page.published && page.publishedAt && (
                      <div>
                        Published:{' '}
                        <span className="text-gray-700">{formatDate(page.publishedAt)}</span>
                      </div>
                    )}
                    {showDetails && (
                      <div>
                        ID: <span className="font-mono">{page.id}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ml-3 flex items-center gap-1">
                  <button
                    onClick={() => copyToClipboard(page.handle)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Copy handle"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  {page.url && (
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                      title="View page"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasNextPage && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <span>📄</span>
              <span>More pages available. Use pagination to fetch additional results.</span>
            </div>
          </div>
        )}
      </div>
    </ContextMenuWrapper>
  )
}
